/**
 * Base-path-aware HTTP helpers. Vite injects BASE_URL from the BASE_PATH setting.
 */
import { apiPath, normalizeBasePath, pagePath, wsPath, type PAGES } from '@clinspace/shared';

export const BASE_PATH = normalizeBasePath(import.meta.env.BASE_URL);

export function api(...segments: string[]): string {
  return apiPath(BASE_PATH, ...segments);
}

export function page(name: keyof typeof PAGES): string {
  return pagePath(BASE_PATH, name);
}

export function wsUrl(): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}${wsPath(BASE_PATH)}`;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly fields?: Record<string, string>,
  ) {
    super(message);
  }
}

/**
 * How long a GET may take before it fails. Without a limit a request to a server that
 * accepts the connection but never answers hangs forever (2026-10-06: the portal sat on
 * "Connecting" after the API server was restarted under an open tab).
 */
export const GET_TIMEOUT_MS = 12_000;

export async function getJson<T>(
  path: string,
  params?: Record<string, string | number | undefined>,
  timeoutMs = GET_TIMEOUT_MS,
): Promise<T> {
  const url = new URL(path, location.origin);
  if (params) {
    for (const [k, v] of Object.entries(params))
      if (v !== undefined && v !== '') url.searchParams.set(k, String(v));
  }
  let res: Response;
  try {
    res = await fetch(url.toString(), {
      headers: { accept: 'application/json' },
      signal: AbortSignal.timeout(timeoutMs),
    });
  } catch (err) {
    if ((err as Error).name === 'TimeoutError')
      throw new ApiError(0, `The server did not answer within ${timeoutMs / 1000} s`);
    throw err;
  }
  return handle<T>(res);
}

export async function sendJson<T>(
  method: 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  headers: Record<string, string> = {},
): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: { 'content-type': 'application/json', accept: 'application/json', ...headers },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return handle<T>(res);
}

async function handle<T>(res: Response): Promise<T> {
  const text = await res.text();
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    data = undefined;
  }
  if (!res.ok) {
    const body = (data ?? {}) as { error?: string; fields?: Record<string, string> };
    throw new ApiError(res.status, body.error ?? `${res.status} ${res.statusText}`, body.fields);
  }
  return data as T;
}
